import { useEffect, useState } from "react";

import {
  api,
  type AnalyticsOverviewResponse,
  type DailyReviewResponse,
  type RiskStatsResponse,
} from "../api/client";
import DataExport from "../components/analytics/DataExport";
import {
  EquityPanel,
  ExitMixPanel,
  GatesPanel,
  KpiRow,
  RolesPanel,
  VersionComparePanel,
  WindowPicker,
} from "../components/analytics/OverviewPanels";
import RiskStats from "../components/analytics/RiskStats";
import AppShell from "../components/layout/AppShell";
import PageOwner from "../components/common/PageOwner";
import Panel from "../components/common/Panel";
import { ErrorState, LoadingState } from "../components/common/states";

/**
 * SPEC §14 Analytique — le juge du forward-test. De haut en bas :
 *
 *   1. KPI      — combien, maintenant (fenêtre + aujourd'hui).
 *   2. Equity   — comment ça s'est construit + les ÈRES de paramètres à côté
 *                 (le verdict : quel jeu de params gagne).
 *   3. Mécanique — rôles / mix de sorties / refus / distribution pire-cas §7.
 *   4. Export   — CSV.
 *
 * Un seul sélecteur de fenêtre scope tout sauf les ères (toutes périodes,
 * délibéré). La liste des cycles vit dans Historique ; la heatmap win-rate a
 * été SUPPRIMÉE (le timing/bias-gating est falsifié — étude NO-GO 2026-07-14 —
 * et n/cellule était minuscule) ; la qualité du flux vit dans Journal (infra).
 */
export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState<AnalyticsOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(false);

  const [riskStats, setRiskStats] = useState<RiskStatsResponse | null>(null);
  const [riskStatsLoading, setRiskStatsLoading] = useState(true);
  const [riskStatsError, setRiskStatsError] = useState(false);

  const [dailyReview, setDailyReview] = useState<DailyReviewResponse | null>(null);

  // Le fetch primaire de la page — re-fetch au changement de fenêtre ; la frame
  // précédente reste affichée en opacité réduite (pas de saut de squelette).
  useEffect(() => {
    let cancelled = false;
    setOverviewLoading(true);
    setOverviewError(false);
    api
      .analyticsOverview(days)
      .then((res) => {
        if (cancelled) return;
        setOverview(res);
        setOverviewLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setOverview(null);
        setOverviewError(true);
        setOverviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  useEffect(() => {
    let cancelled = false;
    api
      .riskStats()
      .then((res) => {
        if (cancelled) return;
        setRiskStats(res);
        setRiskStatsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRiskStatsError(true);
        setRiskStatsLoading(false);
      });
    // La tuile « aujourd'hui » est best-effort — son absence ne bloque jamais la page.
    api
      .dailyReview()
      .then((res) => {
        if (!cancelled) setDailyReview(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell>
      <div className="stagger flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* Variante B (2026-07-31) : page du moteur — l'analytique Commandes
                recevra ses onglets quand les stats HUMAN_BASELINE existeront. */}
            <PageOwner variant="auto" />
            <WindowPicker days={days} onChange={setDays} />
          </div>
        </div>

        {overviewError && (
          <Panel aria-label="Erreur analytique">
            <ErrorState title="Vue analytique indisponible" testId="overview-error">
              Le store analytique n'a pas répondu — les chiffres seraient périmés ou manquants.
            </ErrorState>
          </Panel>
        )}
        {overview == null && overviewLoading && (
          <Panel aria-label="Chargement analytique">
            <LoadingState lines={4} label="Chargement de l'analytique…" testId="overview-loading" />
          </Panel>
        )}

        {overview && (
          <div className={overviewLoading ? "opacity-60 transition-opacity" : undefined}>
            <div className="flex flex-col gap-4">
              <KpiRow overview={overview} daily={dailyReview} />

              <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
                <div className="xl:col-span-2">
                  <EquityPanel overview={overview} />
                </div>
                <VersionComparePanel overview={overview} />
              </div>

              <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 xl:grid-cols-4">
                <RolesPanel overview={overview} />
                <ExitMixPanel overview={overview} />
                <GatesPanel overview={overview} />
                <Panel title="Pire-cas §7" aria-label="Distribution du pire-cas">
                  <RiskStats data={riskStats} loading={riskStatsLoading} error={riskStatsError} />
                </Panel>
              </div>
            </div>
          </div>
        )}

        <Panel title="Export" aria-label="Export de données" aside={<span>CSV</span>}>
          <DataExport />
        </Panel>
      </div>
    </AppShell>
  );
}
